import React, { useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import {
  RedeemMagicLinkDocument,
  RedeemMagicLinkMutation,
  RedeemMagicLinkMutationVariables,
} from "generated/graphql";
import { useAuth } from "@components/auth-provider/AuthProvider";
import { normalizeTokenQueryParam } from "@modules/authentication/tokenNormalization";
import { Routes } from "~/routes";

const Authenticate = () => {
  const appUrl = import.meta.env.VITE_APP_URL;
  if (appUrl) {
    try {
      const appHostname = new URL(appUrl).hostname;
      if (window.location.hostname !== appHostname) {
        window.location.replace(`${appUrl}/authenticate${window.location.search}`);
        return null;
      }
    } catch {
      // Malformed VITE_APP_URL — skip redirect and handle auth on this domain.
    }
  }

  const params = new URLSearchParams(window.location.search);
  const email = params.get("email") ?? "";
  const token = normalizeTokenQueryParam(params.get("token")) ?? "";

  const auth = useAuth();
  const [redeemMagicLink, redeemMagicLinkMutation] = useMutation<
    RedeemMagicLinkMutation,
    RedeemMagicLinkMutationVariables
  >(RedeemMagicLinkDocument);

  useEffect(() => {
    if (!email || !token) {
      return;
    }

    redeemMagicLink({
      variables: {
        input: {
          email,
          token,
        },
      },
    }).catch(() => {
      // handled below via redeemMagicLinkMutation.error
    });
  }, [email, token, redeemMagicLink]);

  // Store the token once when the mutation succeeds. The ref guard prevents
  // re-calling setAuthToken on subsequent renders (the `auth` context value
  // changes identity every render because its functions aren't memoized).
  const tokenAppliedRef = useRef(false);
  useEffect(() => {
    if (
      !redeemMagicLinkMutation.loading &&
      redeemMagicLinkMutation.data &&
      !tokenAppliedRef.current
    ) {
      tokenAppliedRef.current = true;
      auth.setAuthToken(redeemMagicLinkMutation.data?.redeemMagicLink ?? "");
    }
  }, [redeemMagicLinkMutation.data, redeemMagicLinkMutation.loading, auth]);

  // All redirects use the render path so the target route's guard always
  // sees committed state. Auth check comes first so an already-authenticated
  // user hitting /authenticate without params goes Home, not Login.
  if (auth.isAuthenticated) {
    return <Navigate to={Routes.Home} replace />;
  }

  if (!email || !token) {
    return <Navigate to={Routes.Login} replace />;
  }

  if (!redeemMagicLinkMutation.loading && redeemMagicLinkMutation.error) {
    return <>Error authenticating; link already used. Please request a new magic link.</>;
  }

  if (redeemMagicLinkMutation.loading) {
    return <>Loading...</>;
  }

  return <>Redirecting...</>;
};

export default Authenticate;
